import { createSlice } from '@reduxjs/toolkit';

import type {
  CpfCnpjBalanceSnapshot,
  IntegracaoAlerta,
  SaudeConfigResponse,
  SaudeSnapshot,
  WhatsappHealthSnapshot,
} from '../../types/saudeIntegracoes';
import {
  fetchAlertasIntegracoes,
  fetchSaudeConfig,
  fetchSaudeIntegracoes,
  refreshSaudeIntegracoes,
  updateSaudeConfig,
} from './actions';

const alertasEqual = (a: IntegracaoAlerta[], b: IntegracaoAlerta[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].id !== b[i].id ||
      a[i].nivel !== b[i].nivel ||
      a[i].titulo !== b[i].titulo ||
      a[i].descricao !== b[i].descricao
    ) {
      return false;
    }
  }
  return true;
};

const whatsappEqual = (
  a: WhatsappHealthSnapshot | null,
  b: WhatsappHealthSnapshot | null,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.qualityRating === b.qualityRating &&
    a.status === b.status &&
    a.deliveryRate24h === b.deliveryRate24h &&
    a.totalEnviadas24h === b.totalEnviadas24h &&
    a.totalFalhas24h === b.totalFalhas24h &&
    a.totalEntregues24h === b.totalEntregues24h &&
    a.totalTemplates === b.totalTemplates &&
    a.templatesProblematicos === b.templatesProblematicos &&
    a.providerReachable === b.providerReachable
  );
};

const balanceEqual = (
  a: CpfCnpjBalanceSnapshot | null,
  b: CpfCnpjBalanceSnapshot | null,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.saldo === b.saldo &&
    a.saldoContratado === b.saldoContratado &&
    a.percentual === b.percentual &&
    a.providerReachable === b.providerReachable
  );
};

const snapshotEqual = (a: SaudeSnapshot | null, b: SaudeSnapshot | null): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    whatsappEqual(a.whatsapp, b.whatsapp) &&
    balanceEqual(a.cpf, b.cpf) &&
    balanceEqual(a.cnpj, b.cnpj) &&
    a.capturadoEm.whatsapp === b.capturadoEm.whatsapp &&
    a.capturadoEm.cpf === b.capturadoEm.cpf &&
    a.capturadoEm.cnpj === b.capturadoEm.cnpj
  );
};

interface SaudeIntegracoesState {
  snapshot: SaudeSnapshot | null;
  alertas: IntegracaoAlerta[];
  config: SaudeConfigResponse | null;
  loading: boolean;
  refreshing: boolean;
  savingConfig: boolean;
  error: string | null;
}

const initialState: SaudeIntegracoesState = {
  snapshot: null,
  alertas: [],
  config: null,
  loading: false,
  refreshing: false,
  savingConfig: false,
  error: null,
};

const applyOverview = (
  state: SaudeIntegracoesState,
  payload: { snapshot: SaudeSnapshot; alertas: IntegracaoAlerta[] },
) => {
  if (!snapshotEqual(state.snapshot, payload.snapshot)) {
    state.snapshot = payload.snapshot;
  }
  if (!alertasEqual(state.alertas, payload.alertas)) {
    state.alertas = payload.alertas;
  }
};

const saudeIntegracoesSlice = createSlice({
  name: 'saudeIntegracoes',
  initialState,
  reducers: {
    resetSaudeIntegracoes: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSaudeIntegracoes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSaudeIntegracoes.fulfilled, (state, action) => {
        state.loading = false;
        applyOverview(state, action.payload);
      })
      .addCase(fetchSaudeIntegracoes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Erro desconhecido';
      })
      .addCase(fetchAlertasIntegracoes.fulfilled, (state, action) => {
        if (!alertasEqual(state.alertas, action.payload.alertas)) {
          state.alertas = action.payload.alertas;
        }
      })
      .addCase(fetchAlertasIntegracoes.rejected, (state, action) => {
        state.error = action.payload ?? 'Erro desconhecido';
      })
      .addCase(refreshSaudeIntegracoes.pending, (state) => {
        state.refreshing = true;
      })
      .addCase(refreshSaudeIntegracoes.fulfilled, (state, action) => {
        state.refreshing = false;
        applyOverview(state, action.payload);
      })
      .addCase(refreshSaudeIntegracoes.rejected, (state, action) => {
        state.refreshing = false;
        state.error = action.payload ?? 'Erro desconhecido';
      })
      .addCase(fetchSaudeConfig.fulfilled, (state, action) => {
        state.config = action.payload;
      })
      .addCase(updateSaudeConfig.pending, (state) => {
        state.savingConfig = true;
      })
      .addCase(updateSaudeConfig.fulfilled, (state) => {
        state.savingConfig = false;
      })
      .addCase(updateSaudeConfig.rejected, (state, action) => {
        state.savingConfig = false;
        state.error = action.payload ?? 'Erro desconhecido';
      });
  },
});

export const { resetSaudeIntegracoes } = saudeIntegracoesSlice.actions;
export const saudeIntegracoesReducer = saudeIntegracoesSlice.reducer;
